import { gql } from '@apollo/client/core'
import {
  BaseThreadUserDto,
  bsConstants,
  CreateMessageThreadDto,
  MessageThreadDto,
  MessageThreadGetArgs, MessageThreadPagedResultDto,
  UpdateMessageThreadDto
} from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Replaces `MessageThreadService`.
 *
 * `apiControllerPath` was already empty — threads are pure graphql — so the `useBaseCrud` spread is
 * here for the shared surface and `getApiControllerUrl` would build a url no controller serves.
 *
 * `getOtherThreadParticipant` is the odd one out: no api call, just picks whichever end of a thread
 * is not the current user. It stays on the service because every consumer already holds the service
 * and nothing else wants it; stage 7 is where it would become a composable helper.
 */

const crud = useBaseCrud<MessageThreadDto>('')

const messageThreadFragments = {
  MessageThread: gql`
    fragment MessageThreadFragment on MessageThreadDto {
      id,
      reviewerUserId,
      reviewerUser {
        id,
        name,
        image,
        existingThreadCount
      },
      authorUser {
        id,
        name,
        image,
        existingThreadCount,
        canAcceptMessages,
        canSendMessages
      }
      arc {
        id,
        book {
          id,
          bookCover,
          title,
          seriesName,
          seriesNumber,
          authorPenName {
            id,
            userId,
            name,
            image
          }
        },
        arcTeam {
          id,
          name,
          image
        }
      },
      createdDate,
      userId,
      state,
      lastMessageDate,
      lastMessageText,
      messageCount,
      unreadMessageCount,
      blocked
    }
  `
}

const get = (params?: MessageThreadGetArgs): Promise<MessageThreadPagedResultDto> => {
  return useApollo().query({
    variables: {
      ...params
    },
    query: gql`
      query GetMessageThreads (
        $state: Int
        $needle: String
        $reviewUserId: Int,
        $sortBy: String,
        $descending: Boolean,
        $skip: Int,
        $take: Int
      ) {
        messageThreads (
          state: $state,
          needle: $needle,
          reviewUserId: $reviewUserId,
          sortBy: $sortBy,
          descending: $descending
          skip: $skip,
          take: $take
        ) {
          totalRows,
          items {
            ...MessageThreadFragment
          }
        }
      }
      ${messageThreadFragments.MessageThread}
    `
  })
}

const getById = (id?: number): Promise<MessageThreadDto> => {
  return getByIdForUser(id)
}

const getByIdForUser = (id?: number, forUserId?: number): Promise<MessageThreadDto> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id,
      forUserId
    },
    query: gql`
      query GetMessageThread (
        $id: Int!
        $forUserId: Int
      ) {
        messageThread (id: $id, forUserId: $forUserId) {
          ...MessageThreadFragment,
          arc {
            id,
            userId,
            claims {
              review {
                id,
                rating,
                sites {
                  site,
                  status,
                  link
                }
              }
            }
          }
        }
      }
      ${messageThreadFragments.MessageThread}
    `
  })
}

const create = (model: CreateMessageThreadDto): Promise<MessageThreadDto> => {
  return useApollo().mutate({
    variables: {
      ...model,
      recipientUserId: model.reviewUserId
    },
    mutation: gql`
      mutation CreateMessageThread (
        $arcId: Int!
        $recipientUserId: Int!
      ) {
        createMessageThread (
          arcId: $arcId,
          reviewUserId: $recipientUserId
        ) {
          ...MessageThreadFragment
        }
      }
      ${messageThreadFragments.MessageThread}
    `
  })
}

const update = (model: UpdateMessageThreadDto): Promise<MessageThreadDto> => {
  return useApollo().mutate({
    variables: {
      ...model
    },
    mutation: gql`
      mutation UpdateMessageThread (
        $id: Int!
        $state: Int!
      ) {
        updateMessageThread (
          id: $id,
          state: $state
        ) {
          ...MessageThreadFragment
        }
      }
      ${messageThreadFragments.MessageThread}
    `
  })
}

const deleteThread = (id?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation DeleteMessageThread (
        $id: Int!
      ) {
        deleteMessageThread(id: $id) {
          id
        }
      }
    `
  })
}

const markAsRead = (threadId: number): Promise<MessageThreadDto> => {
  return update({
    id: threadId,
    state: bsConstants.MESSAGES.STATE.READ
  })
}

const markAsUnread = (threadId: number): Promise<MessageThreadDto> => {
  return update({
    id: threadId,
    state: bsConstants.MESSAGES.STATE.NEW
  })
}

const getUnreadThreadCount = (): Promise<number> => {
  return useApollo().query({
    query: gql`
      query GetUnreadThreadCount {
        unreadThreadCount
      }
    `
  })
}

const blockUser = (userId: number, reportUser: boolean): Promise<undefined> => {
  return useApollo().mutate({
    variables: {
      userId,
      reportUser
    },
    mutation: gql`
      mutation BlockUser (
        $userId: Int!
        $reportUser: Boolean!
      ) {
        blockUser(userId: $userId, reportUser: $reportUser)
      }
    `
  })
}

const unblockUser = (userId: number): Promise<undefined> => {
  return useApollo().mutate({
    variables: {
      userId
    },
    mutation: gql`
      mutation UnblockUser (
        $userId: Int!
      ) {
        unblockUser(userId: $userId)
      }
    `
  })
}

const getOtherThreadParticipant = (thread: MessageThreadDto, currentUserId: number): BaseThreadUserDto => {
  return thread.reviewerUserId === currentUserId
    ? thread.authorUser
    : { image: '', name: '', ...thread.reviewerUser }
}

const getHelp = (threadId: number): Promise<undefined> => {
  return useApollo().mutate({
    variables: {
      threadId
    },
    mutation: gql`
      mutation GetThreadHelp (
        $threadId: Int!
      ) {
        getThreadHelp (threadId: $threadId) {
          id
        }
      }
    `
  })
}

/**
 * `globalLoader` is chained by the thread list, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get,
  getById,
  getByIdForUser,
  create,
  update,
  delete: deleteThread,
  markAsRead,
  markAsUnread,
  getUnreadThreadCount,
  blockUser,
  unblockUser,
  getOtherThreadParticipant,
  getHelp,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function messageThreadService () {
  return service
}
